import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef } from "react";
import { ErrorIcon, WarningIcon } from "./Icon";
import "./TextInput.css";

interface IProps {
	id?: string;
	type: "text" | "email" | "password" | "number" | "search"
	placeholder?: string;
	label?: string | JSX.Element;
	required?: boolean;
	value: string;
	htmlRef?: any;
	error?: string | JSX.Element;
	warning?: string | JSX.Element;
	autocomplete?: "email" | "password" | "new-password" | "current-password" | "party-room";
	leftIcon?: JSX.Element;
	focus?: boolean;
	name?: string;
	options?: string[];
	setValue(value: string): void;
	onEnter?(): void;
	onSelect?(value: string): void;
}

const TextInput: FunctionComponent<IProps> = (props) => {
	const { id, type, placeholder, label, name, required, value, htmlRef, error, warning, autocomplete, leftIcon, focus, options, setValue, onEnter, onSelect } = props;
	const hasLabel = !!label;
	const placeholderValue = !hasLabel ? placeholder : "";
	const element = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!focus) { return; }
		element.current?.focus();
	}, [focus]);
	
	const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.value.endsWith("\xa0")) {
			setValue(event.target.value.substring(0, event.target.value.length - 1));
			onSelect?.(event.target.value.substring(0, event.target.value.length - 1));
			return;
		}
		setValue(event.target.value);
	}, [onSelect, setValue]);

	const onKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			onEnter?.();
		}
	}, [onEnter]);

	const labelClass = useMemo(() => {
		if (!value) { return "centeredLabel"; }
		return "titleLabel";
	}, [value]);

	const onScroll = useCallback(() => {
		if (type !== "number") { return; }
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		  }
	}, [type]);

	const message = useMemo(() => {
		if (error) {
			return <>
				<span className="errorIcon"><ErrorIcon /></span>
				<span className="errorLabel">{error}</span>
			</>;
		}
		if (warning) {
			return <>
				<span className="warningIcon"><WarningIcon /></span>
				<span className="warningLabel">{warning}</span>
			</>;
		}
		return null;
	}, [error, warning]);

	return <div className={"textInput" + ((hasLabel) ? " withLabel" : "") + ((leftIcon) ? " withLeftIcon" : "")}>
		<input id={id} autoComplete={autocomplete} list={options ? `${id}datalist` : undefined} name={name} className={error && "error"} type={type} placeholder={placeholderValue} required={required} value={value} onChange={onChange} onKeyPress={onKeyPress} onWheel={onScroll} ref={element} />
		<label className={labelClass}>{label}</label>
		{message}
		{leftIcon && <span className="leftIcon">{leftIcon}</span>}
		{options && <datalist id={`${id}datalist`}>
			{options.map(o => {
				return <option key={o} value={o + "\xa0"} />
			})}	
		</datalist>}
	</div>;
};

export default TextInput;